import { FmtpCustomer } from './fmtpCustomer';
import { FMTPRequestBody } from './requestBody';

export enum ScanSourceConnectors {
    LT1 = 'LT1',
    TV = 'TV',
    EDW = 'EDW',
    TV_RETURNS = 'TV_RETURNS',
    SHIP = 'SHIP',
}

export type GetParcelSearchParameters = FMTPRequestBody & {
    parcelNumber?: string;
    endPointNumber?: string;
    manifestNumber?: string;
    partNumber?: string;
    startDate?: string;
    endDate?: string;
    customerId?: string | number;
    customerFmtpId?: string;
    pageSize?: number;
    sortOrder?: ParcelSearchSortItems;
    sortDirection?: SortDirection;
    sourceOffset: ConnectorOffsets;
};

export type ConnectorOffsets = { [key in ScanSourceConnectors]?: number };

export enum ParcelSearchSortItems {
    CUSTOMER = 'Customer',
    ENDPOINT = 'Endpoint',
    CONTAINER_TYPE = 'ContainerType',
    DISTRESS = 'Distress',
    POSSESSION_DATE = 'PossessionDate',
    DELIVERY_DATE = 'DeliveryDate',
    PARCEL_CODE = 'ParcelCode',
}

export enum SortDirection {
    ASCENDING = 'asc',
    DESCENDING = 'desc',
}

export type ParcelId = {
    sourceSystem: string;
    id: string;
};

export type GeneralParcelInfo = {
    parcelId: ParcelId[];
    customer: CustomerInfo;
    containerType: string;
    deliveryDateLocal: Date;
    deliveryDateUTC: Date;
    distress: boolean;
    endPointInfo: ParcelEndpointInfo;
    manifest: string;
    origin: string;
    parcelCode: string;
    partNumber: string;
    possessionDateLocal: Date;
    possessionDateUTC: Date;
    weight: string;
    deliveryLocationAlert: boolean;
    dateCreated: Date;
};

export type ParcelEndpointInfo = {
    endPointNumber: string;
    endPointName: string;
    endPointCity: string;
    endPointState: string;
    endPointStreet: string;
    endPointZip: string;
    endpointLatitude?: string;
    endpointLongitude?: string;
};

export type CustomerInfo = {
    customerId: string;
    customerName: string;
    customerCode: string;
    parentId: string | null;
};

export type ParcelDetails = {
    logoUrl: string;
    generalParcelInfo: Partial<GeneralParcelInfo>;
    scans: Partial<ParcelDetailScan>[];
    containerHistory: Partial<ParcelDetailContainerHistory>[];
    nestedContainerHistory: Partial<ParcelDetailNestedContainerHistory>[];
    shippingLoadHistory: Partial<ParcelDetailShippingLoadHistory>[];
    generatedLabelHistory: Partial<ParcelDetailGeneratedLabelHistory>[];
    exports: Partial<ParcelDetailExport>[];
    imports: Partial<ParcelDetailImport>[];
    timeline: Partial<ParcelDetailTimeline>[];
};

export type ParcelDetailContainerHistory = {
    shippingUnitNumber: string;
    dateAdded: Date;
    dateRemoved: Date;
    type: string;
    scans: Partial<ParcelDetailScan>[];
    shippingLoadHistory: Partial<ParcelDetailShippingLoadHistory>[];
    nestedContainer: Partial<ParcelDetailNestedContainerHistory>[];
};

export type ParcelDetailNestedContainerHistory = {
    parcelCode: string;
    dateAdded: Date;
    dateRemoved: Date;
};

export type ParcelDetailShippingLoadHistory = {
    loadDate: Date;
    loadedAt: string;
    loadedBy: string;
    route: string;
    departure: Date;
    arrival: Date;
};

export type ParcelDetailGeneratedLabelHistory = {
    labelGeneratorFormat: string;
    recipientNumber: string;
    shippingUnitNumber: string;
    labelCreated: Date;
    expirationDate: Date;
    batchConfirmationNumber: string;
    labelNumber: string;
    comment: string;
};

export type ParcelDetailExport = {
    type: string;
    dateQueued: Date;
    dateSent: Date;
    additionalData: string;
};

export type ParcelDetailImport = {
    type: string;
    date: Date;
    fileName: string;
};

export type ParcelDetailTimeline = {
    date: Date; // This is the display
    eventText: string;
    dateTimezone: 'Local' | 'UTC';
};

export type ParcelDetailScan = {
    scanDateLocal: Date;
    scanDateUtc: Date;
    scanType: string;
    scanSubType: string;
    containerType: string;
    endPointNumber: string;
    locationNumber: string;
    locationName: string;
    addressStreet1: string;
    addressCity: string;
    addressState: string;
    damage: string;
    dateSentAtUtc: Date;
    dateSentAtLocal: Date;
    scannedAt: string;
    scannedBy: string;
    mapLatitude: string;
    mapLongitude: string;
    scanningSolution: ScanSourceConnectors;
};

export type ParcelIdScanHistory = {
    barcode: string;
    customer: CustomerInfo;
    endpoint: string;
    scanType: string;
    parcelType: string;
    date: Date;
    scanningSolution: ScanSourceConnectors;
    scanningSolutionSpecificId: string | number | null;
};

export type ParcelSearchScans = {
    parcelCode: string;
    sourceSystem: ScanSourceConnectors;
    containerParcelCode: string;
    containerType: string;
    freeAstrayFlag: string;
    customer: FmtpCustomer;
    destinationLocationCode: string;
    originLocationCode: string;
    manifest: string;
    partNumber: string;
    scanTimeUtc: Date;
    scanTimeLocal: Date;
    scanType: string;
    weight: string;
    weightUOM: string;
    destinationStopName: string;
    destinationAddressLineOne: string;
    destinationAddressLineTwo: string;
    destinationCity: string;
    destinationState: string;
    destinationPostalCode: string;
};

export type FmtpUniversalParcelId = {
    parcelCode: string;
    customerId: string;
    startDateUtc: Date | string;
    endDateUtc?: Date | string;
};

// Parcel Search v2

export type v2_parcelSearchResult = {
    parcelId: string;
    parcelCode: string;
    endPointInfo: ParcelEndpointInfo;
    customer: FmtpCustomer;

    asnDateLocal?: Date;
    asnDateUtc?: Date;
    possessionDateLocal?: Date;
    possessionDateUtc?: Date;
    deliveryDateLocal?: Date;
    deliveryDateUtc?: Date;
    parcelEvents: v2_parcelSearchEvent[];

    manifest?: string;
    distress: boolean;
    deliveryLocationAlert: boolean;
    origin?: string;
    containerParcelCode?: string;
    containerType?: string;
    partNumber?: string;
    weight?: string;
    freightType?: string; // Refers to the parcel itself, not the container
};

export type v2_parcelSearchEvent = {
    parcelCode: string;
    customer: FmtpCustomer;
    identifier: {
        sourceSystem: ScanSourceConnectors; // e.g. 'LT1'
        sourceSystemMemo?: string;
        sourceSystemEventId: string; // Row ID of the scan. 'TableName:RowId'
        sourceSystemParcelId: string; // e.g. Scan Family Id, or Shipping Unit ID
        isFromDataWarehouse?: boolean; // true if the scan is from the data warehouse
    }[];

    scanTimeUtc: Date;
    scanTimeLocal?: Date;
    dateSentAtUtc: Date;
    dateSentAtLocal?: Date;

    eventType: FmtpEventType;
    scanSubType?: string;

    containerParcelCode?: string;
    containerType?: string;
    freightType?: string; // Refers to the parcel itself, not the container

    freeAstrayFlag: boolean;
    originLocationCode?: string;

    destinationLocationCode?: string;
    destinationCustomerId?: string;
    destinationAddressLineOne?: string;
    destinationAddressLineTwo?: string;
    destinationCity?: string;
    destinationPostalCode?: string;
    destinationState?: string;
    destinationStopName?: string;
    destinationLatitude?: string;
    destinationLongitude?: string;

    currentLocationCode?: string;
    currentCustomerId?: string;
    currentAddressLineOne?: string;
    currentAddressLineTwo?: string;
    currentCity?: string;
    currentPostalCode?: string;
    currentState?: string;
    currentStopName?: string;

    latitude?: string;
    longitude?: string;

    scannedAt?: string;
    scannedBy?: string;

    manifest?: string;
    partNumber?: string;
    weight?: string;

    scanImage?: string;
    scanImageStatus?: 'Passed' | 'Failed' | undefined;
};

export enum FmtpEventType {
    RETAINED = 'RETAINED',
    PICKUP = 'PICKUP',
    ORIGIN_FREE_ASTRAY = 'ORIGIN FREE ASTRAY',
    INBOUND = 'INBOUND',
    ROUTE_FREE_ASTRAY = 'ROUTE FREE ASTRAY',
    DELIVERY = 'DELIVERY',
    LOAD = 'LOAD',
    DISTRESS = 'DISTRESS',
    DAMAGE = 'DAMAGE',
    EXCEPTION = 'EXCEPTION',
    LABEL_CANCEL = 'LABEL CANCEL',
    STOP_DEPART = 'STOP DEPART',
    CONTAINERIZE = 'CONTAINERIZE',
    ARRIVAL = 'ARRIVAL',
    NO_SCAN = 'NO SCAN',
    LABEL_CREATE = 'LABEL CREATE',
    ASN = 'ASN',
}

export type v2_parcelSearchParameters = {
    startDate?: Date;
    endDate?: Date;
    parcelCode?: string;
    customer?: FmtpCustomer;
    hydratedCustomers?: FmtpCustomer[];
    customerId?: string;
    endpoint?: string;
    partNumber?: string;
    manifestNumber?: string;

    sortBy?: v2_sortBy;
    sortDirection?: SortDirection;
};

export type v2_sortBy = keyof Omit<
    v2_parcelSearchResult,
    'parcelEvents' | 'parcelId' | 'containerParcelCode'
>;

export type v2_parcelSearchParcelDetails = {
    generalParcelInfo: v2_parcelSearchResult;
    timeline: ParcelDetailTimeline[];
    containerHistory: Partial<ParcelDetailContainerHistory>[];
    nestedContainerHistory: Partial<ParcelDetailNestedContainerHistory>[];
    shippingLoadHistory: Partial<ParcelDetailShippingLoadHistory>[];
    generatedLabelHistory: Partial<ParcelDetailGeneratedLabelHistory>[];
    exports: Partial<ParcelDetailExport>[];
    imports: Partial<ParcelDetailImport>[];
};
